// src/context/CartContext.tsx
"use client";

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";

export interface CartItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number;
  originalPrice?: number;
  image?: string;
  quantity: number;
  unit?: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: any, quantity?: number, forceSetQuantity?: number, clearPrevious?: boolean) => void;
  removeFromCart: (productId: number | string) => void;
  updateQuantity: (productId: number | string, delta: number) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function parseProductPrice(price: any): number {
  if (typeof price === "number") return price;
  if (!price) return 0;
  const cleaned = String(price).replace(/[^0-9.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

export function CartProvider({ children, subdomain }: { children: ReactNode; subdomain: string }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const sub = subdomain || "nextlook";
  const cartKey = `xylo_cart_${sub}`;
  const softCartKey = `soft_cart_${sub}`;

  // ১. লোকালস্টোরেজ থেকে কার্ট লোড
  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(cartKey) ||
        localStorage.getItem(softCartKey) ||
        localStorage.getItem("cart") ||
        localStorage.getItem("xylo_cart");

      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setCart(parsed);
        } else if (parsed && Array.isArray(parsed.items)) {
          setCart(parsed.items);
        } else {
          setCart([]);
        }
      }
    } catch (e) {
      console.error("Cart load error:", e);
      setCart([]);
    } finally {
      setIsLoaded(true);
    }
  }, [cartKey, softCartKey]);

  // ২. কার্ট স্টেট পরিবর্তিত হলে লোকালস্টোরেজে সব কী সিঙ্ক করা (চেকআউটের জন্যও)
  useEffect(() => {
    if (isLoaded) {
      try {
        const serialized = JSON.stringify(cart);
        localStorage.setItem(cartKey, serialized);
        localStorage.setItem(softCartKey, serialized);
        localStorage.setItem("xylo_cart", serialized);
        localStorage.setItem("cart", serialized);
        localStorage.setItem("soft_cart", serialized);

        window.dispatchEvent(new Event("cartUpdated"));
        window.dispatchEvent(new Event("storage"));
      } catch (e) {
        console.error("Cart save error:", e);
      }
    }
  }, [cart, isLoaded, cartKey, softCartKey]);

  // ৩. 🌟 অ্যাড টু কার্ট কোর লজিক (clearPrevious = true দিলে পুরোনো সব ডিলেট হয়ে শুধু এটি থাকবে)
  const addToCart = useCallback((product: any, quantity = 1, forceSetQuantity?: number, clearPrevious = false) => {
    if (!product) return;

    const prodId = product.id ?? product._id ?? product.pk ?? product.product_id;
    if (prodId === undefined || prodId === null) return;

    const rawPrice = product.price ?? product.discount_price ?? product.sale_price;
    const rawRegPrice = product.compare_price ?? product.regular_price ?? product.price;

    const parsedPrice = parseProductPrice(rawPrice);
    const parsedRegPrice = parseProductPrice(rawRegPrice);

    const price = parsedPrice > 0 ? parsedPrice : parsedRegPrice;
    const originalPrice = parsedRegPrice > price ? parsedRegPrice : price;

    const name = product.name || product.title || "Product";
    const slug = product.slug || String(prodId);
    const unit = product.unit || "1 pc";

    const image =
      product.image ||
      product.primary_image ||
      (Array.isArray(product.images) && product.images[0]?.image) ||
      (Array.isArray(product.images) && typeof product.images[0] === "string" ? product.images[0] : "") ||
      product.thumbnail ||
      "";

    setCart((prev) => {
      // 🌟 Buy Now এর জন্য: যদি clearPrevious True হয়, কার্ট সম্পূর্ণ ক্লিয়ার করে শুধু নতুনটা বসাবে
      if (clearPrevious) {
        return [
          {
            id: prodId,
            name,
            slug,
            price,
            originalPrice,
            image,
            quantity: forceSetQuantity !== undefined ? forceSetQuantity : quantity,
            unit,
          },
        ];
      }

      const currentCart = Array.isArray(prev) ? [...prev] : [];
      const existingIdx = currentCart.findIndex((item) => String(item.id) === String(prodId));

      if (existingIdx > -1) {
        currentCart[existingIdx] = {
          ...currentCart[existingIdx],
          quantity: forceSetQuantity !== undefined ? forceSetQuantity : currentCart[existingIdx].quantity + quantity,
        };
        return currentCart;
      }

      return [
        ...currentCart,
        {
          id: prodId,
          name,
          slug,
          price,
          originalPrice,
          image,
          quantity: forceSetQuantity !== undefined ? forceSetQuantity : quantity,
          unit,
        },
      ];
    });

    setIsCartOpen(true);
  }, []);

  const removeFromCart = useCallback((productId: number | string) => {
    setCart((prev) => (Array.isArray(prev) ? prev.filter((item) => String(item.id) !== String(productId)) : []));
  }, []);

  const updateQuantity = useCallback((productId: number | string, delta: number) => {
    setCart((prev) => {
      if (!Array.isArray(prev)) return [];
      return prev
        .map((item) => {
          if (String(item.id) === String(productId)) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[];
    });
  }, []);

  const clearCart = useCallback(() => setCart([]), []);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const totalItems = Array.isArray(cart) ? cart.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0;
  const subtotal = Array.isArray(cart) ? cart.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0) : 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        subtotal,
        isCartOpen,
        setIsCartOpen,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    return {
      cart: [],
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      totalItems: 0,
      subtotal: 0,
      isCartOpen: false,
      setIsCartOpen: () => {},
      openCart: () => {},
      closeCart: () => {},
    };
  }
  return context;
};